import React from "react";
import { DateTime } from "luxon";
import { formatCurrency } from "../../utils/currency";
import DateInputWithPicker from "../DateInputWithPicker";
import TimeInputWithPicker from "../TimeInputWithPicker";
import { splitDateTimeLocal, joinDateTimeLocal } from "../../utils/datetimeLocal";

/**
 * TicketSetupForm - Step 2 of ticket creation wizard
 * Configure ticket types, pricing, and sales settings
 */
const TicketSetupForm = ({ formData, updateFormSection, isLoading, setError, soldCounts, orgCurrency = "AUD" }) => {
    const ticketSetup = formData.ticketSetup || {};
    const ticketTypes = ticketSetup.ticketTypes || [];
    const salesPeriod = ticketSetup.salesPeriod || {};
    // Sales end must be after sales start (a blank end is valid — it means "sell until the
    // event starts"). Surfaced inline; the backend also blocks publishing an invalid window.
    const salesWindowInvalid = !!(salesPeriod.startDate && salesPeriod.endDate
        && new Date(salesPeriod.endDate) <= new Date(salesPeriod.startDate));

    // For a published event, each ticket type's quantity can't be reduced below the
    // number already sold (enforced server-side too). Returns the floor for a type, or 0.
    const soldFloor = (ticketType) =>
        (soldCounts && ticketType?.name && soldCounts[ticketType.name]) || 0;

    // Validation functions
    const validateTicketType = (ticket) => {
        return {
            name: !!ticket.name?.trim(),
            description: true, // Optional field - always valid
            // Guard against "" — an empty string coerces "" >= 0 to true, so it would
            // otherwise pass as a valid price (matches the settings-page guard).
            price: ticket.price !== undefined && ticket.price !== null && ticket.price !== "" && Number(ticket.price) >= 0,
            quantity: ticket.quantity !== undefined && ticket.quantity !== null && ticket.quantity > 0
        };
    };

    const validateAllTicketTypes = () => {
        if (ticketTypes.length === 0) return false;
        return ticketTypes.every(ticket => {
            const validation = validateTicketType(ticket);
            return validation.name && validation.price && validation.quantity;
        });
    };

    const getFieldError = (ticketIndex, field) => {
        const ticket = ticketTypes[ticketIndex];
        if (!ticket) return false;
        
        const validation = validateTicketType(ticket);
        return !validation[field];
    };

    const handleTicketTypesChange = newTicketTypes => {
        updateFormSection("ticketSetup", {
            ...ticketSetup,
            ticketTypes: newTicketTypes,
        });
    };

    const handleSalesPeriodChange = (field, value) => {
        updateFormSection("ticketSetup", {
            ...ticketSetup,
            salesPeriod: {
                ...salesPeriod,
                [field]: value,
            },
        });
    };

    // Update one half (date or time) of a combined "YYYY-MM-DDTHH:MM" sales-period
    // field, preserving the other half and writing back the merged value.
    const handleSalesPeriodPartChange = (field, part, partValue) => {
        const current = splitDateTimeLocal(salesPeriod[field]);
        const next = part === "date"
            ? joinDateTimeLocal(partValue, current.time)
            : joinDateTimeLocal(current.date, partValue);
        handleSalesPeriodChange(field, next);
    };

    const handleRefundPolicyChange = value => {
        updateFormSection("ticketSetup", {
            ...ticketSetup,
            refundPolicy: value,
        });
    };

    // Add new ticket type
    const addTicketType = () => {
        const newTicketType = {
            id: Date.now(), // Temporary ID for React keys
            name: "",
            price: 0,
            // currency is NOT set here — the backend stamps the org base currency
            // (org.currency) onto every ticket type on save (createOrUpdateDraft).
            quantity: 1,
            description: "",
            restrictions: "",
        };

        handleTicketTypesChange([...ticketTypes, newTicketType]);
    };

    // Remove ticket type
    const removeTicketType = index => {
        const newTicketTypes = ticketTypes.filter((_, i) => i !== index);
        handleTicketTypesChange(newTicketTypes);
    };

    // Update individual ticket type
    const updateTicketType = (index, field, value) => {
        const newTicketTypes = [...ticketTypes];
        newTicketTypes[index] = {
            ...newTicketTypes[index],
            [field]: value,
        };
        handleTicketTypesChange(newTicketTypes);
    };

    return (
        <div className="ticket-setup-form">
            <h2>Ticket Setup</h2>
            <p className="form-description">Configure your ticket types, pricing, and sales period.</p>

            {/* Ticket Types Section */}
            <div className="form-section">
                <div className="section-header">
                    <h3>Ticket Types</h3>
                    <button type="button" onClick={addTicketType} className="btn-turquoise">
                        + Add Ticket Type
                    </button>
                </div>

                {ticketTypes.length > 0 && ticketTypes.some(t => t.name) && (
                    <div className="defaults-applied-notice">
                        <small style={{ color: "var(--dl-color-orange-default)", fontWeight: "500" }}>
                            ✓ Your default ticket type applied
                        </small>
                    </div>
                )}

                {ticketTypes.length === 0 && (
                    <div className="no-ticket-types">
                        <p>No ticket types added yet. Click "Add Ticket Type" to get started.</p>
                    </div>
                )}

                {ticketTypes.map((ticketType, index) => (
                    <div key={ticketType.id || index} className="ticket-type-card">
                        <div className="ticket-type-header">
                            <h4>{ticketType.description}</h4>
                            <button
                                type="button"
                                onClick={() => removeTicketType(index)}
                                className="btn-orange btn-small"
>
                                Remove
                            </button>
                        </div>

                        <div className="ticket-type-form">
                            <div className="form-row">
                                <div className="form-group half-width">
                                    <label htmlFor={`ticket-name-${index}`}>Ticket Name *</label>
                                    <input
                                        id={`ticket-name-${index}`}
                                        type="text"
                                        value={ticketType.name || ""}
                                        onChange={e => updateTicketType(index, "name", e.target.value)}
                                        placeholder="e.g., General Admission, VIP"
                                        className={`form-input ${getFieldError(index, 'name') ? 'error' : ''}`}
                                    />
                                </div>
                                <div className="form-group half-width">
                                    <label htmlFor={`ticket-price-${index}`}>Price ({orgCurrency}) *</label>
                                    <input
                                        id={`ticket-price-${index}`}
                                        type="text"
                                        inputMode="decimal"
                                        value={ticketType.price === "" || ticketType.price === undefined || ticketType.price === null ? "" : ticketType.price}
                                        onFocus={e => e.target.select()}
                                        onChange={e => {
                                            // type="text" so non-numeric input actually reaches here
                                            // (a number input swallows it). Strip non-numeric, then store
                                            // "" for empty/invalid so validation rejects it instead of
                                            // silently coercing to a free $0 ticket. $0 typed is still valid.
                                            const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                                            const parsed = parseFloat(cleaned);
                                            updateTicketType(index, "price", cleaned === "" || isNaN(parsed) ? "" : parsed);
                                        }}
                                        className={`form-input ${getFieldError(index, 'price') ? 'error' : ''}`}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group half-width">
                                    <label htmlFor={`ticket-quantity-${index}`}>Available Quantity *</label>
                                    {soldFloor(ticketType) > 0 && (
                                        <p className="form-hint">
                                            {soldFloor(ticketType)} sold so far, so the minimum is {soldFloor(ticketType)}.
                                        </p>
                                    )}
                                    <input
                                        id={`ticket-quantity-${index}`}
                                        type="number"
                                        min={Math.max(1, soldFloor(ticketType))}
                                        value={ticketType.quantity || ""}
                                        onChange={e => {
                                            const floor = Math.max(1, soldFloor(ticketType));
                                            const entered = parseInt(e.target.value) || floor;
                                            updateTicketType(index, "quantity", Math.max(floor, entered));
                                        }}
                                        className={`form-input ${getFieldError(index, 'quantity') ? 'error' : ''}`}
                                    />
                                </div>
                                <div className="form-group half-width">
                                    <label htmlFor={`ticket-restrictions-${index}`}>Restrictions</label>
                                    <input
                                        id={`ticket-restrictions-${index}`}
                                        type="text"
                                        value={ticketType.restrictions || ""}
                                        onChange={e => updateTicketType(index, "restrictions", e.target.value)}
                                        placeholder="e.g., 18+, Must bring ID"
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label htmlFor={`ticket-description-${index}`}>Description</label>
                                    <textarea
                                        id={`ticket-description-${index}`}
                                        value={ticketType.description || ""}
                                        onChange={e => updateTicketType(index, "description", e.target.value)}
                                        placeholder="e.g., Reserved seating, welcome drink, event materials included"
                                        className="form-textarea"
                                        rows={2}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Sales Period Section */}
            <div className="form-section">
                <h3>Sales Period</h3>
                <div className="form-row">
                    <div className="form-group half-width">
                        <label>Sales Start</label>
                        <small className="form-help">Leave empty to start sales immediately</small>
                        <div className="datetime-picker-pair">
                            <DateInputWithPicker
                                id="sales-start-date"
                                value={splitDateTimeLocal(salesPeriod.startDate).date}
                                onChange={dateVal => handleSalesPeriodPartChange("startDate", "date", dateVal)}
                                placeholder="Select date"
                                disablePast={false}
                            />
                            <TimeInputWithPicker
                                id="sales-start-time"
                                value={splitDateTimeLocal(salesPeriod.startDate).time}
                                onChange={timeVal => handleSalesPeriodPartChange("startDate", "time", timeVal)}
                            />
                        </div>
                    </div>
                    <div className="form-group half-width">
                        <label>Sales End</label>
                        <small className="form-help">Leave empty to sell until event starts</small>
                        <div className="datetime-picker-pair">
                            <DateInputWithPicker
                                id="sales-end-date"
                                value={splitDateTimeLocal(salesPeriod.endDate).date}
                                onChange={dateVal => handleSalesPeriodPartChange("endDate", "date", dateVal)}
                                placeholder="Select date"
                                disablePast={false}
                                error={salesWindowInvalid}
                                fallbackMonth={splitDateTimeLocal(salesPeriod.startDate).date
                                    ? DateTime.fromISO(splitDateTimeLocal(salesPeriod.startDate).date)
                                    : null}
                            />
                            <TimeInputWithPicker
                                id="sales-end-time"
                                value={splitDateTimeLocal(salesPeriod.endDate).time}
                                onChange={timeVal => handleSalesPeriodPartChange("endDate", "time", timeVal)}
                            />
                        </div>
                        {salesWindowInvalid && (
                            <small className="form-help" style={{ color: 'var(--dl-color-warning-red)' }}>
                                Sales end must be after sales start.
                            </small>
                        )}
                    </div>
                </div>
            </div>

            {/* Refund Policy Section */}
            <div className="form-section">
                <h3>Refund Policy</h3>
                <div className="form-row">
                    <div className="form-group full-width">
                        <label htmlFor="refund-policy">Refund Policy *</label>
                        <textarea
                            id="refund-policy"
                            value={ticketSetup.refundPolicy || ""}
                            onChange={e => handleRefundPolicyChange(e.target.value)}
                            placeholder="A default policy is provided. Edit to customize your refund terms..."
                            className="form-textarea"
                            rows={3}
                        />
                        <small className="form-help">
                            A standard refund policy is applied by default. You can customize it above to match your
                            event's specific terms.
                        </small>
                    </div>
                </div>
            </div>

            {/* Ticket Summary */}
            {ticketTypes.length > 0 && (
                <div className="ticket-summary">
                    <h4>Ticket Summary</h4>
                    <div className="summary-table">
                        <div className="summary-header">
                            <span>Type</span>
                            <span>Price</span>
                            <span>Quantity</span>
                            <span>Total Value</span>
                        </div>
                        {ticketTypes.map((ticket, index) => (
                            <div key={ticket.id || index} className="summary-row">
                                <span>{ticket.name || `Ticket ${index + 1}`}</span>
                                <span>{ticket.price ? formatCurrency(ticket.price, orgCurrency) : "Free"}</span>
                                <span>{ticket.quantity || 0}</span>
                                <span>
                                    {formatCurrency((ticket.price || 0) * (ticket.quantity || 0), orgCurrency)}
                                </span>
                            </div>
                        ))}
                        <div className="summary-total">
                            <span>
                                <strong>Total Tickets:</strong>
                            </span>
                            <span>
                                <strong>{ticketTypes.reduce((sum, ticket) => sum + (ticket.quantity || 0), 0)}</strong>
                            </span>
                            <span>
                                <strong>Max Revenue:</strong>
                            </span>
                            <span>
                                <strong>
                                    {formatCurrency(
                                        ticketTypes.reduce((sum, ticket) => sum + (ticket.price || 0) * (ticket.quantity || 0), 0),
                                        orgCurrency
                                    )}
                                </strong>
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Form Validation Summary */}
            {ticketTypes.length > 0 && !validateAllTicketTypes() && (
                <div className="form-validation">
                    <h4>⚠️ Please complete required fields:</h4>
                    <ul>
                        {ticketTypes.map((ticket, index) => {
                            const validation = validateTicketType(ticket);
                            const isValid = validation.name && validation.price && validation.quantity;
                            if (isValid) return null;
                            
                            const missingFields = [];
                            if (!validation.name) missingFields.push("name");
                            if (!validation.price) missingFields.push("price");
                            if (!validation.quantity) missingFields.push("quantity");
                            
                            return (
                                <li key={ticket.id || index} className="invalid">
                                    <strong>Ticket {index + 1}</strong>: Missing {missingFields.join(", ")}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default TicketSetupForm;
